import type { ExpressPaymentType } from "@stripe/stripe-js";

import {
  PaymentFieldConfig,
  WalletFieldConfig,
} from "@evergiving/common/dist/es2015/form";
import { PaymentSourceKind } from "@evergiving/common/dist/es2015/pledge";

/**
 * The payment sources that settle through Stripe's Express Checkout Element.
 * `stripePayment` is not one of them: it is the deprecated kind meaning "some
 * wallet", which cannot say which button the donor pressed.
 */
export type WalletKind = PaymentSourceKind.ApplePay | PaymentSourceKind.GooglePay;

export const isWalletKind = (
  kind: PaymentSourceKind | undefined
): kind is WalletKind =>
  kind === PaymentSourceKind.ApplePay || kind === PaymentSourceKind.GooglePay;

/**
 * Stripe names the wallet that was used on the Express Checkout confirm event.
 * Reading it is what lets the submitted payment source say which wallet paid,
 * so the submission lambda can check it against what the link authorises.
 *
 * Anything the form did not ask for returns undefined, and the caller must
 * fail the payment rather than submit it under a kind the link may not
 * authorise.
 */
export const walletKindFor = (
  expressPaymentType: ExpressPaymentType
): WalletKind | undefined => {
  if (expressPaymentType === "apple_pay") {
    return PaymentSourceKind.ApplePay;
  }

  if (expressPaymentType === "google_pay") {
    return PaymentSourceKind.GooglePay;
  }

  return undefined;
};

/**
 * The config block a wallet is rendered from. Each wallet is its own payment
 * source with its own block, so this is the 1:1 lookup the renderer needs.
 */
export const walletConfigFor = (
  payment: PaymentFieldConfig,
  kind: WalletKind
): WalletFieldConfig | undefined =>
  kind === PaymentSourceKind.ApplePay ? payment.applePay : payment.googlePay;
